
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '../api';
import { useAuth } from './AuthContext';

const PharmacyContext = createContext(null);

export const usePharmacy = () => {
  const ctx = useContext(PharmacyContext);
  if (!ctx) throw new Error('usePharmacy must be used within PharmacyProvider');
  return ctx;
};

export const PharmacyProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  const [pharmacyDetails, setPharmacyDetails] = useState(() => {
    // Seed from localStorage so pages render instantly before network
    try {
      const cached = localStorage.getItem('pharmacyDetails');
      return cached ? JSON.parse(cached) : {
        name: localStorage.getItem('pharmacyName') || 'Pharmacy',
        pan: '', phone: '', email: '', address: ''
      };
    } catch {
      return { name: 'Pharmacy', pan: '', phone: '', email: '', address: '' };
    }
  });

  const [profileLoaded, setProfileLoaded] = useState(false);

  const fetchPharmacyProfile = useCallback(async () => {
    try {
      const { data } = await API.get('/users/profile');
      const details = {
        name: data.pharmacyName || data.name || 'Pharmacy',
        pan: data.pan || '',
        phone: data.phone || '',
        email: data.email || '',
        address: data.address || '',
        role: data.role || 'admin',
      };
      setPharmacyDetails(details);
      localStorage.setItem('pharmacyDetails', JSON.stringify(details));
      setProfileLoaded(true);
      return details;
    } catch (err) {
      console.error('PharmacyContext: failed to fetch profile', err);
      setProfileLoaded(true);
    }
  }, []);

  // Refresh profile when auth state changes (e.g. login)
  useEffect(() => {
    if (isAuthenticated) {
      fetchPharmacyProfile();
    }
  }, [isAuthenticated, fetchPharmacyProfile]);

  // Expose a method for pages that update profile (ProfilePage) to refresh the cache
  const refreshPharmacyProfile = useCallback(() => {
    return fetchPharmacyProfile();
  }, [fetchPharmacyProfile]);

  return (
    <PharmacyContext.Provider value={{ pharmacyDetails, profileLoaded, refreshPharmacyProfile }}>
      {children}
    </PharmacyContext.Provider>
  );
};

export default PharmacyContext;
